import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { api, money, fdate } from "../../lib/api";
import { useAuth, isStaff } from "../../lib/auth";
import { StatusBadge } from "../../components/StatusBadge";
import { HostView, BillingView, SignaturesView, RegSignaturesView } from "./HostBilling";

const LeaderView = () => {
  const [tours, setTours] = useState([]);
  useEffect(() => { api.get("/leader/tours").then((r) => setTours(r.data)); }, []);
  return tours.map((t) => (
    <div key={t.id} className="card mt-6 p-6" data-testid={`leader-tour-${t.code}`}>
      <div className="flex flex-wrap items-center justify-between gap-3"><div><p className="eyebrow">{t.code}</p><h2 className="text-3xl font-semibold">{t.title}</h2><p className="text-sm text-slate-500">Departs {fdate(t.start_date)} · {t.registered}/{t.capacity} registered</p></div><Link to={`/tours/${t.code}`} className="btn-outline btn-sm">View tour page</Link></div>
      <table className="tbl mt-4"><thead><tr><th>Traveler</th><th>Status</th></tr></thead><tbody>{t.roster.map((r, i) => <tr key={i}><td>{r.name}</td><td><StatusBadge status={r.status} /></td></tr>)}</tbody></table>{!t.roster.length && <p className="p-4 text-sm text-slate-500" data-testid="leader-empty">No travelers have registered yet.</p>}
      <p className="mt-3 text-xs text-slate-500">As group leader you see your group's names and registration status only — payments, passports and documents stay private to each traveler.</p>
    </div>
  ));
};

export default function Portal() {
  const { user, logout } = useAuth();
  const [bs, setBs] = useState(null);
  useEffect(() => { api.get("/portal/bookings").then((r) => setBs(r.data)); }, []);
  if (isStaff(user)) return <Navigate to="/staff" replace />;
  return (
    <div className="container-x py-12" data-testid="traveler-portal">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">Traveler portal</p><h1 className="mt-2 text-4xl font-semibold sm:text-5xl">Welcome, {user.name.split(" ")[0]}</h1></div>
        <button className="btn-ghost" onClick={logout} data-testid="portal-logout-btn"><LogOut className="h-4 w-4" />Sign out</button>
      </div>
      {user.role === "group_leader" && <LeaderView />}
      <SignaturesView />
      <RegSignaturesView />
      <HostView ids={user.host_tour_ids} />
      <BillingView />
      <h2 className="mt-10 text-2xl font-semibold">My bookings</h2>
      {bs && bs.length === 0 && <div className="card mt-4 p-8 text-center text-slate-600" data-testid="portal-empty">No bookings yet. Enter your tour code in <b>Find Your Tour</b> at the top of the page to register.</div>}
      <div className="mt-4 grid gap-5 md:grid-cols-2">
        {(bs || []).map((b) => (
          <Link key={b.id} to={b.status === "draft" ? `/register/${b.tour_code}` : `/portal/bookings/${b.id}`} className="card group flex overflow-hidden hover:shadow-lg" data-testid={`portal-booking-${b.tour_code}`}>
            <img src={b.tour?.header?.image} alt="" className="hidden w-40 object-cover sm:block" />
            <div className="flex-1 p-5">
              <div className="flex items-center justify-between gap-2"><span className="text-xs font-semibold text-slate-500">Tour {b.tour_code}{b.ref && ` · ${b.ref}`}</span><StatusBadge status={b.status} /></div>
              <h3 className="mt-1 text-2xl font-semibold">{b.tour?.title}</h3>
              <p className="text-sm text-slate-500">{fdate(b.tour?.start_date)} · {b.traveler_ids.length} traveler(s)</p>
              {b.status === "draft" ? <p className="mt-3 text-sm font-semibold" style={{ color: "var(--brand-accent)" }}>Continue registration →</p> : b.money && <p className="mt-3 text-sm">Balance <b>{money(b.money.balance)}</b> of {money(b.money.total)}</p>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
